import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DropdownOption } from './types';
import { Box, StyledDropdownBox } from './Box';
import { useDropdownLogic } from './useDropdownLogic';
import { createPortal } from 'react-dom';
import { Menu } from './Menu';
import { IconName } from '../icon';
import { DropdownParentContext } from './DropdownParentContext';
import { keyCodes } from '../utils/keyCodes';
import { useDebouncedInputState, useMergedRefs } from '../hooks';
import { ComponentWithCssSelector, withCssSelector } from '../shared/withCssSelector';
import { PropsWithClassName } from '../tabselector/PropsWithClassName';
import { formInputSizes, FormInputSize } from '../utils/formInputSize';
import {
	DropdownPlaceholder, DropdownSearchIcon, DropdownTextInput, DropdownValue, DropdownValueIcon
} from './Dropdown.styled';

type DropdownProps<T extends React.Key> = {
	value: T | undefined,
	icon?: IconName,
	loading?: boolean,
	onClose?: () => void,
	onOpen?: () => void,
	onSelect: (id: T) => unknown,
	options: DropdownOption<T>[],
	restricted?: boolean,
	placeholder?: string,
	searchLabel?: string,
	loadingLabel?: string,
	searchable?: boolean,
	disabled?: boolean,
	hasError?: boolean,
	size?: FormInputSize,
	tabbable?: boolean,
};

function DropdownWithRef<T extends React.Key>({
	value,
	icon: defaultIcon,
	loading,
	onClose: propsOnClose,
	onOpen: propsOnOpen,
	onSelect,
	restricted,
	options = [],
	placeholder,
	searchLabel,
	loadingLabel,
	searchable,
	disabled,
	className,
	hasError,
	size = formInputSizes.default,
	tabbable,
}: PropsWithClassName<DropdownProps<T>>, boxExternalRef: React.ForwardedRef<HTMLDivElement>) {
	const [
		searchInputValue, 
		searchPhrase,
		onSearchChange,
		forceSearchPhraseChange
	] = useDebouncedInputState();

	const onMenuClose = () => {
		forceSearchPhraseChange('');
		propsOnClose?.();
	};

	const onMenuOpen = useCallback(() => {
		propsOnOpen?.();
	}, [propsOnOpen]);

	const {
		isOpen,
		styles,
		attributes,
		onClose,
		onOpen,
		buttonRef,
		menuRef,
	} = useDropdownLogic({ onMenuOpen, onMenuClose, sticky: true });

	const selectedItem = value ? options.find(option => option.value === value) : null;

	const searchMode = isOpen && searchable;

	// Add original index to options BEFORE filtering to handle duplicates consistently
	const optionsWithIndex = useMemo(() =>
		options.map((option, idx) => ({ ...option, originalIndex: idx })),
	[options]);

	const filteredOptions = useMemo(() => searchMode && searchPhrase ?
		optionsWithIndex.filter((option) => option.label.toLowerCase().includes(searchPhrase.toLowerCase())) :
		optionsWithIndex,
	[optionsWithIndex, searchMode, searchPhrase]);

	const onValueSelected = useCallback((v: T) => {
		onSelect(v);
		onClose();
	}, [onSelect, onClose]);

	// ----- keyboard nav state (only active when tabbable) -----
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

	useEffect(() => {
		if (!isOpen) {
			setHighlightedIndex(-1);
			return;
		}
		const idx = value !== undefined ? filteredOptions.findIndex(o => o.value === value) : -1;
		setHighlightedIndex(idx);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	useEffect(() => {
		if (isOpen) setHighlightedIndex(-1);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchPhrase]);

	const moveHighlight = useCallback((dir: 1 | -1) => {
		setHighlightedIndex(prev => {
			let next = prev === -1 ? (dir === 1 ? 0 : filteredOptions.length - 1) : prev + dir;
			while (next >= 0 && next < filteredOptions.length && filteredOptions[next]?.disabled) {
				next += dir;
			}
			if (next < 0 || next >= filteredOptions.length) return prev;
			return next;
		});
	}, [filteredOptions]);

	const selectHighlighted = useCallback(() => {
		if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
			const option = filteredOptions[highlightedIndex];
			if (!option.disabled) onValueSelected(option.value);
		}
	}, [highlightedIndex, filteredOptions, onValueSelected]);

	const boxKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
		if (disabled || loading) return;
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			if (isOpen) { if (highlightedIndex >= 0) selectHighlighted(); else onClose(); }
			else onOpen();
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (!isOpen) onOpen(); else moveHighlight(1);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (isOpen) moveHighlight(-1);
		} else if (e.key === 'Escape' && isOpen) {
			e.preventDefault();
			onClose();
		}
	}, [disabled, loading, isOpen, highlightedIndex, selectHighlighted, onClose, onOpen, moveHighlight]);

	const searchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); moveHighlight(1); }
		else if (e.key === 'ArrowUp') { e.preventDefault(); e.stopPropagation(); moveHighlight(-1); }
		else if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); selectHighlighted(); }
		else if (e.key === keyCodes.ESC) { e.stopPropagation(); onClose(); }
	}, [moveHighlight, selectHighlighted, onClose]);

	const highlightedOriginalIndex = tabbable && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length
		? filteredOptions[highlightedIndex].originalIndex
		: undefined;

	const { parent } = useContext(DropdownParentContext);

	const boxRef = useMergedRefs(buttonRef, boxExternalRef);

	const displayIcon = selectedItem?.icon || defaultIcon;

	return <>
		<Box
			ref={boxRef}
			loading={loading}
			size={size}
			isOpen={isOpen}
			onOpen={onOpen}
			onClose={onClose}
			onKeyDown={boxKeyDown}
			disabled={disabled}
			restricted={restricted}
			className={className}
			hasError={hasError}
			tabbable={tabbable}>
			{searchMode
				? <>
					{!selectedItem && <DropdownSearchIcon icon='magnifying-glass' />}
					<DropdownTextInput
						value={searchInputValue}
						onChange={onSearchChange}
						autoFocus
						type='text'
						onKeyDown={tabbable ? searchKeyDown : undefined}
						placeholder={selectedItem?.label || searchLabel || 'Search'} />
				</>
				: (selectedItem ?
					<>
						{displayIcon && <DropdownValueIcon icon={displayIcon} />}
						<DropdownValue>
							{selectedItem.label}
						</DropdownValue>
					</>
					: <DropdownPlaceholder>
						{loading ? (loadingLabel || 'Loading') : placeholder || 'Select'}
					</DropdownPlaceholder>)}

		</Box>
		{isOpen && createPortal(<Menu
			popperStyles={styles}
			popperAttributes={attributes}
			onSelect={onValueSelected}
			options={filteredOptions}
			ref={menuRef}
			value={value}
			highlightedOriginalIndex={highlightedOriginalIndex}
		/>, parent || document.body)}
	</>;
}

// eslint-disable-next-line max-len
export const Dropdown = withCssSelector(React.forwardRef(DropdownWithRef), StyledDropdownBox.toString()) as unknown as (<T extends React.Key>(
	props: PropsWithClassName<DropdownProps<T>> & { ref?: React.ForwardedRef<HTMLDivElement> }
  ) => ReturnType<typeof DropdownWithRef>) & ComponentWithCssSelector;

